<template>
  <div class="app-container" v-loading="loading">
    <el-card>
      <div class="back-box">
        <div @click="handleBack">
          <img src="../../../assets/back.png" alt="" />
          <span>返回</span>
        </div>
      </div>

      <Title>基础信息</Title>
      <el-descriptions :column="3" border class="mt10">
        <el-descriptions-item label="子账号姓名">{{ info.childName }}</el-descriptions-item>
        <el-descriptions-item label="子账号手机号"><masked-phone :customer-id="info.childCustomerId" :followup-id="info.id" :masked="info.childPhoneMasked" /></el-descriptions-item>
        <el-descriptions-item label="身份等级">{{ info.identityLevel || '—' }}</el-descriptions-item>
        <el-descriptions-item label="主账号姓名">{{ info.parentName }}</el-descriptions-item>
        <el-descriptions-item label="主账号手机号"><masked-phone :customer-id="info.parentCustomerId" :followup-id="info.id" :masked="info.parentPhoneMasked" /></el-descriptions-item>
        <el-descriptions-item label="申请来源">{{ info.applySourceDesc || '—' }}</el-descriptions-item>
        <el-descriptions-item label="所属大区">{{ info.regionName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="省市区">{{ [info.provinceName, info.cityName, info.areaName].filter(i => i).join('') || '—' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="statusTagType(info.status)">{{ statusText(info.status) }}</el-tag>
          <span v-if="info.subStatus && info.subStatus !== 0" class="sub-status">（{{ subStatusText(info.subStatus) }}）</span>
        </el-descriptions-item>
      </el-descriptions>

      <Title class="mt20">回访结果</Title>
      <el-descriptions :column="3" border class="mt10">
        <el-descriptions-item label="联系结果">{{ contactResultText(info.contactResult) }}</el-descriptions-item>
        <el-descriptions-item label="回访人">{{ info.visitByName || '—' }}</el-descriptions-item>
        <!-- REQ-2026-601171 #4：回访时间显示的是教育部/客服在表单里填的 visitTime（"什么时候联系的客户"），不是提交时刻 visitAt；老数据没有 visitTime 时回落 visitAt，不显示空白。 -->
        <el-descriptions-item label="回访时间">{{ info.visitTime || info.visitAt || '—' }}</el-descriptions-item>
        <el-descriptions-item label="下次跟进时间" v-if="info.nextFollowAt">{{ info.nextFollowAt }}</el-descriptions-item>
      </el-descriptions>

      <template v-if="info.contactResult === 1">
        <Title class="mt20">身份核实</Title>
        <el-descriptions :column="4" border class="mt10">
          <el-descriptions-item label="是否本人申请">{{ boolText(info.qSelfApply) }}</el-descriptions-item>
          <el-descriptions-item label="手机号与资料一致">{{ boolText(info.qPhoneMatch) }}</el-descriptions-item>
          <el-descriptions-item label="是否知晓用途">{{ boolText(info.qKnowPurpose) }}</el-descriptions-item>
          <el-descriptions-item label="关系是否属实">{{ boolText(info.qRelationTrue) }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions :column="1" border class="mt10">
          <el-descriptions-item label="核实结果">{{ verifyResultText(info.verifyResult) }}</el-descriptions-item>
          <el-descriptions-item label="异常类型" v-if="info.verifyResult === 2">
            {{ (info.abnormalTypes || []).map(t => abnormalTypeText(t)).join('、') || '—' }}
          </el-descriptions-item>
          <el-descriptions-item label="反馈说明">{{ info.feedback || '—' }}</el-descriptions-item>
          <el-descriptions-item label="凭证" v-if="(info.evidenceUrls || []).length">
            <el-image v-for="(url, idx) in info.evidenceUrls" :key="idx" :src="url" :preview-src-list="info.evidenceUrls"
                      style="width:70px;height:70px;margin-right:8px" fit="cover" />
          </el-descriptions-item>
        </el-descriptions>
      </template>

      <!-- 测试单 190：市场结论=确认异常后 status 直接置 3（异常已关闭），原来只按 4/5 判，核实提交人/时间/结论就看不到了。
           改成"生成过市场任务就显示"，用 marketTaskNo 判，不再枚举状态 -->
      <template v-if="info.marketTaskNo">
        <Title class="mt20">市场核实</Title>
        <el-descriptions v-if="info.status === 4" :column="1" border class="mt10">
          <el-descriptions-item label="待核实人">{{ (info.pendingMarketReceiverNames || []).join('、') || '暂无待核实人' }}</el-descriptions-item>
        </el-descriptions>
        <el-descriptions :column="3" border class="mt10">
          <el-descriptions-item label="市场任务单号">{{ info.marketTaskNo || '—' }}</el-descriptions-item>
          <el-descriptions-item label="核实提交人">{{ (info.latestMarketVerify && info.latestMarketVerify.submittedByName) || '—' }}</el-descriptions-item>
          <el-descriptions-item label="核实截止时间">{{ info.marketDeadlineAt || '—' }}</el-descriptions-item>
          <el-descriptions-item label="核实完成时间">{{ info.marketVerifiedAt || '—' }}</el-descriptions-item>
          <el-descriptions-item label="市场结论" :span="2">{{ subStatusText(info.subStatus) || '—' }}</el-descriptions-item>
          <!-- 下面这些是市场人员在 App 上真正填的内容。后端一直挂在 ShowVO.latestMarketVerify 上，
               这里原来只取了 submittedByName，其余十来项和证据图用户在后台一项都看不到。 -->
          <el-descriptions-item label="联系情况">{{ mv.contactResultDesc || '—' }}</el-descriptions-item>
          <el-descriptions-item label="核实方式">{{ (mv.verifyMethodsDesc || []).join('、') || '—' }}</el-descriptions-item>
          <el-descriptions-item label="实际联系人">{{ mv.actualContact || '—' }}</el-descriptions-item>
          <el-descriptions-item label="联系时间">{{ mv.contactTime || '—' }}</el-descriptions-item>
          <el-descriptions-item label="申请来源说明" :span="2">{{ mv.applySourceDesc || '—' }}</el-descriptions-item>
          <el-descriptions-item label="问题项" :span="3">{{ (mv.problemItemsDesc || []).join('、') || '—' }}</el-descriptions-item>
          <el-descriptions-item label="核实说明" :span="3">{{ mv.note || '—' }}</el-descriptions-item>
          <el-descriptions-item label="证据图" :span="3">
            <!-- 按凭证类型分组，文案随后端 typeDesc 走（与 App 上传页同一份口径，别在前端再写一套 Map） -->
            <template v-if="(mv.evidence || []).length">
              <div v-for="(group, gi) in groupEvidence(mv)" :key="gi" style="margin-bottom:8px">
                <div style="color:#909399;font-size:12px;margin-bottom:4px">{{ group.label }}</div>
                <el-image v-for="(one, idx) in group.items" :key="idx" :src="one.url"
                          :preview-src-list="evidenceUrls(mv)"
                          style="width:70px;height:70px;margin-right:8px" fit="cover" />
              </div>
            </template>
            <span v-else>—</span>
          </el-descriptions-item>
        </el-descriptions>
      </template>

      <!-- 一个任务可以被核实多次：「仍无法核实」是唯一可重复提交的非终态，
           典型链路是"第一次没核出来 → 第二次才定论"。上面那块只有最新一条，
           做异常复核的人需要看完整的演变链，所以这里列全部。
           顺序与 App 端一致（submitted_at 正序，早的在前）——
           两端同一件事显示成不同顺序，比不显示更容易让人看错。 -->
      <template v-if="(info.marketVerifyRecords || []).length > 1">
        <Title class="mt20">核实记录（{{ info.marketVerifyRecords.length }} 次）</Title>
        <el-card v-for="(rec, ri) in info.marketVerifyRecords" :key="rec.id || ri" shadow="never" class="mt10">
          <div class="verify-rec-hd">
            <span class="verify-rec-no">第 {{ ri + 1 }} 次</span>
            <span class="verify-rec-by">{{ rec.submittedByName || '—' }} · {{ rec.submittedAt || '—' }}</span>
          </div>
          <el-descriptions :column="3" border size="mini" class="mt10">
            <el-descriptions-item label="联系情况">{{ rec.contactResultDesc || '—' }}</el-descriptions-item>
            <el-descriptions-item label="核实方式">{{ (rec.verifyMethodsDesc || []).join('、') || '—' }}</el-descriptions-item>
            <el-descriptions-item label="实际联系人">{{ rec.actualContact || '—' }}</el-descriptions-item>
            <el-descriptions-item label="联系时间">{{ rec.contactTime || '—' }}</el-descriptions-item>
            <el-descriptions-item label="申请来源说明" :span="2">{{ rec.applySourceDesc || '—' }}</el-descriptions-item>
            <el-descriptions-item label="问题项" :span="3">{{ (rec.problemItemsDesc || []).join('、') || '—' }}</el-descriptions-item>
            <el-descriptions-item label="核实结论">{{ rec.conclusionDesc || '—' }}</el-descriptions-item>
            <el-descriptions-item label="核实说明" :span="2">{{ rec.note || '—' }}</el-descriptions-item>
            <el-descriptions-item label="证据图" :span="3">
              <template v-if="(rec.evidence || []).length">
                <div v-for="(group, gi) in groupEvidence(rec)" :key="gi" style="margin-bottom:8px">
                  <div style="color:#909399;font-size:12px;margin-bottom:4px">{{ group.label }}</div>
                  <el-image v-for="(one, idx) in group.items" :key="idx" :src="one.url"
                            :preview-src-list="evidenceUrls(rec)"
                            style="width:70px;height:70px;margin-right:8px" fit="cover" />
                </div>
              </template>
              <span v-else>—</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </template>

      <div class="mt20">
        <el-button @click="goActivities" v-if="[3, 4, 5].includes(info.status) || info.contactResult">流转记录</el-button>
        <el-button @click="goCloseRecord" v-if="[3, 4, 5].includes(info.status)">关闭记录</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import childFollowupApi from '@/api/cust/childFollowup'
import MaskedPhone from './components/MaskedPhone.vue'

const STATUS_MAP = { 1: '待回访', 2: '回访正常', 3: '异常已关闭', 4: '待市场核实', 5: '市场核实完成' }
const STATUS_TAG = { 1: 'warning', 2: 'success', 3: 'info', 4: 'warning', 5: '' }
const SUB_STATUS_MAP = { 1: '确认异常', 2: '误判已澄清', 3: '仍无法核实' }
const CONTACT_RESULT_MAP = { 1: '已接通', 2: '未接通', 3: '无法联系' }
const ABNORMAL_TYPE_MAP = { 1: '本人不知情', 2: '非本人申请', 3: '资料不一致', 4: '手机号不一致', 5: '身份信息异常', 6: '申请目的异常', 7: '关系不属实', 8: '其他' }

export default {
  name: 'SubaccountFollowupShow',
  components: { MaskedPhone },
  props: ['rowId'],
  data() {
    return {
      loading: false,
      info: {}
    }
  },
  computed: {
    // 取不到就给空对象，模板里不用每一项都写 info.latestMarketVerify && ...
    mv() {
      return this.info.latestMarketVerify || {}
    }
  },
  created() {
    this.loadDetail()
  },
  methods: {
    // 证据图按类型分组。typeDesc 由后端给（FollowupConst.EvidenceType），
    // 后端给不出就回落"其他凭证"，不在前端复制一份类型表 —— 复制就会有一天两边对不上。
    //
    // ⚠️ 顶部「最新一条」和下面的「核实记录」列表**共用这一个方法**，
    // 别为历史区再抄一份：两份分组逻辑迟早会走散，而且走散了页面不报错。
    groupEvidence(rec) {
      const list = (rec && rec.evidence) || []
      const order = []
      const bucket = {}
      list.forEach(one => {
        if (!one || !one.url) return
        const label = one.typeDesc || '其他凭证'
        if (!bucket[label]) {
          bucket[label] = []
          order.push(label)
        }
        bucket[label].push(one)
      })
      return order.map(label => ({ label, items: bucket[label] }))
    },
    // 预览列表用这条记录的全部图，点任意一张都能左右翻
    evidenceUrls(rec) {
      return (((rec && rec.evidence) || [])).filter(one => one && one.url).map(one => one.url)
    },
    loadDetail() {
      this.loading = true
      childFollowupApi.fetchById(this.rowId)
        .then(({ data }) => { this.info = data || {} })
        .finally(() => { this.loading = false })
    },
    statusText(status) { return STATUS_MAP[status] || '—' },
    statusTagType(status) { return STATUS_TAG[status] || '' },
    subStatusText(subStatus) { return SUB_STATUS_MAP[subStatus] || '' },
    contactResultText(v) { return CONTACT_RESULT_MAP[v] || '—' },
    abnormalTypeText(v) { return ABNORMAL_TYPE_MAP[v] || v },
    boolText(v) {
      if (v === 1) return '是'
      if (v === 0) return '否'
      return '—'
    },
    verifyResultText(v) {
      if (v === 1) return '正常'
      if (v === 2) return '异常'
      return '—'
    },
    goActivities() {
      this.$emit('handleShow', this.rowId, 'activities')
    },
    goCloseRecord() {
      this.$emit('handleShow', this.rowId, 'closeRecord')
    },
    handleBack() {
      this.$emit('handleShow', '', 'list')
    }
  }
}
</script>

<style lang="scss" scoped>
.verify-rec-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.verify-rec-no {
  font-weight: 600;
  color: #303133;
}
.verify-rec-by {
  color: #909399;
  font-size: 12px;
}

.sub-status {
  color: #e73f3f;
  margin-left: 4px;
}
</style>
